import {
  canAccessWorkspace,
  findWorkspaceBySlug,
  resolveActiveWorkspaceSlug,
  resolveFallbackRoute,
  useAccessContext,
} from '@vizit/access';
import type { MeContext } from '@vizit/api';
import { WORKSPACE_LINK_SEARCH_PARAM, workspaceRoute } from '../../workspace-routes';
import { readLastWorkspaceSlug } from './last-workspace';

// Pre-workspace URLs land here instead of 404-ing. `?workspace=<slug>` pins the target; otherwise:
// last visited workspace, then active workspace, then the highest picker the user can reach.
const LEGACY_FEATURE_SEGMENTS = new Set(['library', 'explore', 'settings', 'reports', 'org-user-management']);

const WORKSPACE_SCOPED_ORGANIZATIONS_PATTERN = /^\/w\/[^/]+(\/organizations(?:\/.*)?)$/;

export type LegacyWorkspaceRedirect =
  | { status: 'none' }
  | { status: 'resolving' }
  | { status: 'redirect'; to: string; search: Record<string, unknown> };

function resolveAccessibleSlug(me: MeContext, slug: string | null): string | null {
  if (!slug) {
    return null;
  }

  const workspace = findWorkspaceBySlug(me, slug);

  return workspace && canAccessWorkspace(me, workspace.id) ? slug : null;
}

export function useLegacyWorkspaceRedirect(
  pathname: string,
  search: Record<string, unknown> = {},
): LegacyWorkspaceRedirect {
  const { me, isLoading } = useAccessContext();

  const organizationsPath = pathname.match(WORKSPACE_SCOPED_ORGANIZATIONS_PATTERN)?.[1];

  if (organizationsPath) {
    return { status: 'redirect', to: organizationsPath, search };
  }

  const firstSegment = pathname.replace(/^\/+/, '').split('/')[0] ?? '';

  if (!LEGACY_FEATURE_SEGMENTS.has(firstSegment)) {
    return { status: 'none' };
  }

  if (isLoading) {
    return { status: 'resolving' };
  }

  if (!me) {
    return { status: 'none' };
  }

  const { [WORKSPACE_LINK_SEARCH_PARAM]: requestedSlug, ...forwardedSearch } = search;

  if (typeof requestedSlug === 'string' && requestedSlug.length > 0) {
    const pinnedSlug = resolveAccessibleSlug(me, requestedSlug);

    return pinnedSlug
      ? { status: 'redirect', to: workspaceRoute(pinnedSlug, pathname), search: forwardedSearch }
      : { status: 'none' };
  }

  const slug = resolveAccessibleSlug(me, readLastWorkspaceSlug()) ?? resolveActiveWorkspaceSlug(me);

  if (slug) {
    return { status: 'redirect', to: workspaceRoute(slug, pathname), search: forwardedSearch };
  }

  const fallbackRoute = resolveFallbackRoute(me);

  return fallbackRoute ? { status: 'redirect', to: fallbackRoute, search: {} } : { status: 'none' };
}
